import React, { useEffect, useMemo, useState } from 'react';
import extractQuestions from 'shared/utils/extractQuestions';

import { SortableTreeWithoutDndContext as SortableTree } from '@nosferatu500/react-sortable-tree';
import { Empty, Layout } from 'antd';

import { gql, useApolloClient, useMutation } from '@apollo/client';
import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import InputInfoCircle from 'components/InputInfoCircle';
import { FormattedMessage } from 'react-intl';
import EditContentModal from '../EditSurveyContent';
import DragToAddContent, { NODE_DND_TYPE } from './DragToAddContent';
import nodeContentRenderer from './SurveyContentTreeNodeItem';

import messages from './messages';
// Custom package does not export it so copied locally
import './react-sortable-tree.css';
import './edit-structure.less';

const QUESTION_TEMPLATES = gql`
  query QuestionTemplates($surveyType: SurveyType!, $questionType: QuestionType!, $questionCategoryTitles: [String!], $surveyCategoryIds: [ID!], $languages: [String!]!) {
    questionTemplates(surveyType: $surveyType, questionType: $questionType, questionCategoryTitles: $questionCategoryTitles, surveyCategoryIds: $surveyCategoryIds, languages: $languages) {
      id
      title
      description
      options {
        id
        title
        value
        group {
          id
        }
      }
      categories {
        id
        title
      }
      survey {
        language
      }
    }
  }
`;

const REMOVE_CONTENT = gql`
  mutation ($id: ID!) {
    removeSurveyContent(id: $id) { status message }
  }
`;

const MOVE_CONTENT = gql`
  mutation ($id: ID!, $surveyId: ID, $parentId: ID, $position: Int!) {
    moveSurveyContent(id: $id, surveyId: $surveyId, parentId: $parentId, position: $position) { id }
  }
`;

const CREATE_PAGE_BREAK = gql`
  mutation ($position: Int!, $parentId: ID!) {
    createPageBreak(position: $position, parentId: $parentId) { id }
  }
`;

const getNodeKey = ({ node }) => node.id || 'new';
const canDrop = ({ node, nextParent }) => !nextParent || (nextParent.block && !node.block);
const canHaveChildren = (node) => node.block;

function toTreeData(block, existingTreeData) {
  const treeData = block?.children?.map(item => ({
    ...item,
    expanded: existingTreeData?.find(i => i.id === item.id)?.expanded ?? true, // keep expanded state
    children: toTreeData(item.block),
  }));

  return treeData;
}

async function getTemplateData(client, survey, translations, node) {
  const data = {};

  if (node.question.categories?.length) {
    const templates = await client.query({
      query: QUESTION_TEMPLATES,
      variables: {
        surveyType: survey.type,
        questionType: node.question.settings.type,
        questionCategoryTitles: node.question.categories,
        surveyCategoryIds: survey.categories.map(c => c.id),
        languages: [survey.language, ...translations.map(t => t.language)],
      },
    });

    if (templates?.data?.questionTemplates?.length) {
      const { questionTemplates } = templates.data;
      const template = questionTemplates.find(t => t.survey.language === survey.language);
      if (template) {
        data.title = template.title;
        data.description = template.description;
        data.options = template.options.map(o => ({
          title: o.title,
          value: o.value,
          translations: translations.reduce((acc, translation) => {
            const translationTemplate = questionTemplates.find(t => t.survey.language === translation.language);
            if (translationTemplate) {
              const translatedOption = translationTemplate.options.find(to => to.group.id === o.group.id);
              if (translatedOption) {
                acc[translation.id] = {
                  title: translatedOption.title,
                  value: translatedOption.value,
                };
              }
            }

            return acc;
          }, {}),
        }));
        data.categories = template.categories;
        data.translations = translations.reduce((acc, translation) => {
          const translationTemplate = questionTemplates.find(t => t.survey.language === translation.language);
          if (translationTemplate) {
            acc[translation.id] = {
              title: translationTemplate.title,
              description: translationTemplate.description,
            };
          }

          return acc;
        }, {});
      }
    }
  }

  if (node.question?.options?.length) {
    data.options = node.question.options.map(option => ({
      ...option,
      translations: option.translations || translations.reduce((acc, translation) => {
        acc[translation.id] = {
          ...option,
        };

        return acc;
      }, {}),
    }));
  }

  return data;
}

const PlaceholderRenderer = (props) => (
  <div className={`rst__placeholder ${props.canDrop && 'rst__placeholderLandingPad'}`}>
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Survey is empty">Drag a question from the sidebar to this area</Empty>
  </div>
);

const getDeleteMessage = node => {
  if (node.question) {
    return messages.confirmDeleteQuestion;
  }

  if (node.block) {
    return messages.confirmDeleteBlock;
  }

  if (node.text) {
    return messages.confirmDeleteText;
  }

  return messages.confirmDeleteContent;
};

const EditStructure = ({ survey, translations, refetch }) => {
  const $t = useFormatMessage();
  const [treeData, setTreeData] = useState([]);
  const questions = useMemo(() => treeData && extractQuestions(treeData), [treeData]);
  const [selectedNode, setSelectedNode] = useState();
  const [removeContentMutation] = useMutation(REMOVE_CONTENT);
  const [moveContent] = useMutation(MOVE_CONTENT, { onCompleted: () => refetch() });
  const [createPageBreak] = useMutation(CREATE_PAGE_BREAK, { onCompleted: () => refetch() });
  const client = useApolloClient();
  const { modal, message } = useGlobalContext();

  const resetTreeData = () => {
    if (survey) {
      setTreeData(toTreeData(survey.root, treeData));
    }
  };

  useEffect(resetTreeData, [survey]);

  const onEditContent = (node, path) => setSelectedNode({ ...node, path });

  const cancelEdit = () => {
    if (!selectedNode.id) {
      resetTreeData();
    }
    setSelectedNode(null);
  };

  const onEditDone = () => {
    refetch();
    cancelEdit();
  };

  const removeContent = async (id) => {
    const { data: { removeSurveyContent } } = await removeContentMutation({ variables: { id } });
    if (removeSurveyContent.status === 'ERROR') {
      message.error(removeSurveyContent.message);
    } else {
      await refetch();
    }
  };

  const onRemoveContent = async (node) => {
    if (node.pageBreak) {
      removeContent(node.id);
    } else {
      modal.confirm({
        title: $t(getDeleteMessage(node)),
        okText: $t(messages.delete),
        okType: 'danger',
        confirmLoading: true,
        onOk: () => removeContent(node.id),
      });
    }
  };

  const onMoveContent = async ({ treeData: data, node, nextParentNode, nextPath }) => {
    const position = nextParentNode
      ? nextParentNode.children.indexOf(node)
      : data.indexOf(node);
    const parentId = (nextParentNode && nextParentNode.block.id) || survey.root.id;

    if (node.id) {
      moveContent({ variables: { id: node.id, position, parentId } });
    } else if (node.pageBreak) {
      createPageBreak({ variables: { position, parentId } });
    } else {
      const newNode = {
        ...node,
        question: node.question && {
          ...node.question,
          ...await getTemplateData(client, survey, translations, node),
        },
        path: nextPath,
        position,
        parentId,
      };

      setSelectedNode(newNode);
    }
  };

  const getNodeProps = () => ({ onEdit: onEditContent, onRemove: onRemoveContent });

  return (
    <>
      <h2>
        Questions
        <InputInfoCircle title={<FormattedMessage {...messages.helpText} />} iconStyle={{ marginLeft: 8, width: 16 }} />
      </h2>
      <Layout>
        <Layout.Sider theme="light">
          <DragToAddContent />
        </Layout.Sider>
        <Layout.Content className="edit-structure">
          <SortableTree
            className="survey-content-tree"
            treeData={treeData}
            getNodeKey={getNodeKey}
            canDrop={canDrop}
            canNodeHaveChildren={canHaveChildren}
            generateNodeProps={getNodeProps}
            onChange={setTreeData}
            onMoveNode={onMoveContent}
            dndType={NODE_DND_TYPE}
            theme={{
              nodeContentRenderer,
              scaffoldBlockPxWidth: 33,
            }}
            placeholderRenderer={PlaceholderRenderer}
          />
          <EditContentModal selectedNode={selectedNode} questions={questions} translations={translations} onCancel={cancelEdit} onEdit={onEditDone} survey={survey} />
        </Layout.Content>
      </Layout>
    </>
  );
};

export default EditStructure;
